import type { AgentConfig } from '@form-factory/schema';
import {
  Agent,
  ChatModel,
  Role,
  SourcesFormat,
} from '@form-factory/schema/enums';
import { useLingui } from '@lingui/react/macro';
import type { TypedFormState } from '@utima/ui-informed';
import { memo } from 'react';

import { Card } from '@/components/card/Card';
import { optionsFromEnum } from '@/components/form/formUtils';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { useEnumTranslations } from '@/hooks';
import { useAccess } from '@/hooks/useAccess';

export type SuperAdminFieldsProps = {};

export const SuperAdminFields = memo(
  function SuperAdminFields({}: SuperAdminFieldsProps) {
    const { t } = useLingui();
    const { hasAccess } = useAccess();
    const translations = useEnumTranslations();

    const { schema } = useFieldsSchema(
      [
        {
          name: 'agent',
          visible: hasAccess([Role.SuperAdmin]),
          label: t`Agent`,
          type: 'select',
          options: {
            [Agent.FormFactory]: t`Form Factory`,
          },
        },
        {
          name: 'model',
          visible: hasAccess([Role.SuperAdmin]),
          label: t`Model`,
          type: 'select',
          options: optionsFromEnum(ChatModel),
        },
        {
          name: 'smallModel',
          visible: hasAccess([Role.SuperAdmin]),
          label: 'Small Model',
          type: 'select',
          options: optionsFromEnum(ChatModel),
        },
        {
          name: 'agentConfig.systemPrompt',
          visible: hasAccess([Role.SuperAdmin]),
          label: t`System prompt`,
          type: 'text',
          uiProps: {
            rows: 10,
          },
          uiControl: 'textarea',
        },
        {
          name: 'agentConfig.hasWebSearch',
          label: t`Web search`,
          type: 'checkbox',
          uiControl: 'switch',
          defaultValue: false,
          visible: hasAccess([Role.SuperAdmin]),
        },
        {
          $relevant: {
            $when: ({ formState }) =>
              (formState?.values as any)?.agentConfig?.showSources,
            $fields: [
              {
                visible: hasAccess([Role.SuperAdmin]),
                name: 'chatbotConfig.sourcesFormat',
                label: t`Sources format`,
                type: 'select',
                options: translations.sourcesFormat,
                defaultValue: SourcesFormat.Simple,
                tooltip: t`This setting will affect the format of the sources in the response.`,
              },
            ],
          },
        },
        {
          $relevant: {
            $when: ({ formState }) =>
              !!(formState as TypedFormState<{ agentConfig: AgentConfig }>)
                .values?.agentConfig?.hasWebSearch,
            $fields: [
              {
                name: 'agentConfig.webSearchUrls',
                label: t`Web search URLs`,
                type: 'text',
                uiProps: {
                  rows: 5,
                },
                uiControl: 'textarea',
                parser: value => {
                  if (typeof value === 'string') {
                    return value
                      .split('\n')
                      .map(line => line.trim())
                      .filter(line => line !== '');
                  }

                  return value;
                },
                mask: value => {
                  if (Array.isArray(value)) {
                    return value.map((item: string) => `${item} \n`).join('');
                  }

                  return value;
                },
              },
            ],
          },
        },
      ],
      [t, hasAccess],
    );

    return (
      <Card title={t`Superadmin configuration`} variant='secondary'>
        <SchemaFields schema={schema} />
      </Card>
    );
  },
);
